// EnvironmentalImpact.js
import React, { useState, useEffect } from 'react';
import AWS from 'aws-sdk';
import "./EI.css"; 
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import DashboardPage from './Dashboard';
import styles from './Dashboard.module.css';

import CoalIcon from '../EI/factory.png';
import CarIcon from '../EI/car.png';
import OilIcon from '../EI/barrel.png';
import TreeIcon from '../EI/leaf.png';
import ForestIcon from '../EI/forest.png';
import CableIcon from '../EI/copper.png';



const KpiCard = ({ title, value, unit, subtitle }) => {
  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <div className={styles.cardValue}>
        {value.toLocaleString()}
        <span className={styles.cardUnit}>{unit}</span>
      </div>
      <p className={styles.cardSubtitle}>{subtitle}</p>
    </div>
  );
};

const ProgressCircle = ({ value, maxValue, text }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const finalPercentage = (value / maxValue) * 100;
    let animationFrameId;

    const animateProgress = () => {
      const increment = finalPercentage / 200; // Slowing down the animation
      setProgress((prevProgress) => {
        const nextProgress = prevProgress + increment;
        if (nextProgress >= finalPercentage) {
          cancelAnimationFrame(animationFrameId); // Stop the animation if final value is reached or exceeded
          return finalPercentage; // Ensure we don't exceed the final value
        }
        return nextProgress;
      });
      animationFrameId = requestAnimationFrame(animateProgress); // Continue the animation
    };

    animationFrameId = requestAnimationFrame(animateProgress); // Start the animation
    return () => cancelAnimationFrame(animationFrameId); // Cleanup
  }, [value, maxValue]);

  const percentage = Math.min(progress, 100);

  return (
    <div className={styles.progressCircle}>
      <CircularProgressbar
        value={percentage}
        text={`${value.toFixed(2)}`}
        styles={buildStyles({
          textColor: '#333',
          pathColor: '#32CD32', // A vibrant light green (LimeGreen)
          trailColor: '#d6d6d6',
          textSize: '16px', // Smaller text size for the percentage value
          transition: 'stroke-dashoffset 0.5s ease 0s',
        })}
      />
      <div className={styles.progressLabel}>{text}</div>
      <div className={styles.progressPercentage}>{`${percentage.toFixed(2)}% Complete`}</div>
      <div className={styles.progressGoal}>Goal: {maxValue.toLocaleString()}</div>
    </div>
  );
};




const formatValue = (value) => {
  if (value >= 1e9) {
      return (value / 1e9).toFixed(2) + ' billion';
  } else if (value >= 1e6) {
      return (value / 1e6).toFixed(2) + ' million';
  } else if (value >= 1e3) {
      return (value / 1e3).toFixed(2) + ' thousand';
  } else {
      return value.toFixed(2);
  }
};

const CO2Equivalents = ({ totalCO2, totalCu }) => {
  const metricTons = totalCO2 * 1;
  const equivalents = [
      { value: (metricTons * 10000/9.07*2.2), label: ' kilograms of coal burned', icon: CoalIcon },
      { value: (metricTons * 10000 * 1.6) / 3.91, label: ' kilometers driven by an average car', icon: CarIcon },
      { value: (metricTons)/0.43, label: 'barrels of crude oil consumed', icon: OilIcon },
      { value: (metricTons ) / 0.06, label: 'tree seedlings grown for 10 years', icon: TreeIcon },
      { value: (metricTons * 1) / 0.58, label: 'acres of forest in one year', icon: ForestIcon },
      { value: (totalCu) / 50, label: 'meters of offshore submarine cable for a wind turbine', icon: CableIcon },
  ];
  const idle_Co2= totalCO2;

  return (
        <div className="co2EquivalentsContainer">
            <div className="totalImpact">
                GIT Climate Impact to Date
            </div>
        <div className="equivalentGrid">
            {equivalents.map((equiv, index) => (
                <div key={index} className="equivalentItem">
                    <img src={equiv.icon} alt={equiv.label} className="equivalentIcon" />
                    <div className="equivalentText">
                        <h3>{formatValue(equiv.value)}</h3>
                        <p>{equiv.label}</p>
                    </div>
                </div>
            ))}
        </div>
    </div>
);
};

function EnvironmentalImpact() {  const [loading, setLoading] = useState(false);
  const [fuelPrices, setFuelPrices] = useState([]);
  const [selectedFuelType, setSelectedFuelType] = useState('All');
  const [fuelTypes, setFuelTypes] = useState([]); 
  const [etuPrice, setEtuPrice] = useState(null);
  const [globalPortsAverage, setGlobalPortsAverage] = useState(null);
  const [globalPortsMGO, setGlobalPortsAverageMGO] = useState(null);
  const [globalPortsIFO, setGlobalPortsAverageIFO] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [liveValue, setLiveValue] = useState(0);
  const [isOpen, setIsOpen] = useState(true); 
  const toggleSection = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
      // Configure AWS
      AWS.config.update({
          accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
          secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
          region: process.env.REACT_APP_AWS_REGION
      });

      // Create a new S3 instance
      const s3 = new AWS.S3();
      
      const fetchS3Data = async () => {
          try {
              const response = await s3.getObject({ Bucket: 'git-dash-bucket', Key: 'ei_results.json' }).promise();
              const jsonData = JSON.parse(response.Body.toString());
              setDashboardData(jsonData);
          } catch (error) {
              console.error('Error fetching data from S3:', error);
          }
      };

      fetchS3Data();

      const fetchETUPrice = async () => {
          try {
              const etuPriceParams = {
                  Bucket: "git-dash-bucket",
                  Key: "ets_price_today.json",
              };
              const etuPriceData = await s3.getObject(etuPriceParams).promise();
              const etuPriceJson = JSON.parse(etuPriceData.Body.toString('utf-8'));
              const priceDate = Object.keys(etuPriceJson)[0]; // Get the date key from the object
              const priceValue = etuPriceJson[priceDate]; // Get the price value using the date key
              setEtuPrice({ date: priceDate, price: priceValue });
              console.log("price", priceValue); // Set both date and price in the state
          } catch (error) {
              console.error('Error fetching ETU price:', error);
          }
      };

      fetchETUPrice();
      
      const fetchFuelPrices = async () => {
          try {
              const fuelParams = {
                  Bucket: "git-dash-bucket",
                  Key: "fuel_prices_today.csv",
              };
              const fuelData = await s3.getObject(fuelParams).promise();
              const csvData = fuelData.Body.toString('utf-8');
              const rows = csvData.split('\n').map(row => row.split(','));
              setFuelPrices(rows);
              // Extracting fuel types from the fuel prices
              const fetchedFuelTypes = [...new Set(rows.map(price => price[1]))];
              setFuelTypes(fetchedFuelTypes); // Removed 'All' from here
              setSelectedFuelType(fetchedFuelTypes[0]); // Set the default selected fuel type

              const vlsfoPrices = rows.filter(row => row[1] === 'VLSFO').map(row => parseFloat(row[2]));
              const globalPortsAverageValue = vlsfoPrices.length > 0 ? vlsfoPrices[0] : null;
              setGlobalPortsAverage(globalPortsAverageValue);

              const mgoPrices = rows.filter(row => row[1] === 'MGO').map(row => parseFloat(row[2]));
              const globalPortsAveragMGO = mgoPrices.length > 0 ? mgoPrices[0] : null;
              setGlobalPortsAverageMGO(globalPortsAveragMGO);

              const ifoPrices = rows.filter(row => row[1] === 'IFO380').map(row => parseFloat(row[2]));
              const globalPortsAveragIFO = ifoPrices.length > 0 ? ifoPrices[0] : null;
              setGlobalPortsAverageIFO(globalPortsAveragIFO);
          } catch (error) {
              console.error('Error fetching fuel prices:', error);
          }
      };

      fetchFuelPrices();
  }, []);

  useEffect(() => {
      const timer = setInterval(() => {
          setLiveValue((prevValue) => prevValue + 0.00360);
      }, 1000);
      return () => clearInterval(timer);
  }, []);

  if (loading) {
      return <p>Loading...</p>;
  }
  
  if (!dashboardData) {
      return <div>Loading...</div>;
  }
  
  const totalCo2Today = (dashboardData.total_CO2_saved_today + liveValue).toFixed(2);
  const totalCo2Cumulative = (dashboardData.total_CO2_saved_cumulative + liveValue).toFixed(2);
  const totalCuCumulative = dashboardData["total Cu saved"].toFixed(2) || 0;

  return (
      <div className="environmental-impact-page">
          <div className="dashboardContainer">
              <div className={styles.cardContainer}>
                  <KpiCard
                      title="Git is saving"
                      value={totalCo2Today}
                      unit="Tons/day"
                      subtitle="Total CO2 saved today"
                  />
                  <KpiCard
                      title="Git has saved"
                      value={totalCuCumulative}
                      unit="Kgs Cu"
                      subtitle="Total Cu saved cumulative"
                  />
                  <KpiCard
                      title="Git has saved"
                      value={totalCo2Cumulative}
                      unit="Tons CO2"
                      subtitle="Total CO2 saved cumulative"
                  />
              </div>
              <div className="progress-circle-container">
                  {Object.keys(dashboardData.yearly_goal_metrics).map((metric) => {
                      let labelText = '';
                      switch(metric) {
                          case 'avoided_CO2':
                              labelText = 'Avoided CO2 eq. (tons)';
                              break;
                          case 'avoided_VOC':
                              labelText = 'Avoided VOC (kg)';
                              break;
                          case 'avoided_NOX_SOX':
                              labelText = 'Avoided NOx (tons)';
                              break;
                          case 'avoided_Si':
                              labelText = 'Avoided Silicon (kg)';
                              break;
                          case 'avoided_Cu':
                              labelText = 'Avoided Cu (kg)';
                              break;
                          // Add other cases as needed
                          default:
                              labelText = `${metric} (${dashboardData.yearly_goal_metrics[metric].unit})`; // Use the original metric name if no specific label is provided
                      }

                      return (
                          <ProgressCircle
                              key={metric}
                              value={dashboardData.yearly_goal_metrics[metric].saved}
                              maxValue={dashboardData.yearly_goal_metrics[metric].goal}
                              text={labelText}
                          />
                      );
                  })}
              </div>
              <CO2Equivalents totalCO2={totalCo2Cumulative} totalCu={totalCuCumulative} />
          </div>
          
          <div className="fuelHeading">
            <h2>Fuel Prices Today</h2>
            </div>

          <div
        className="toggle-section"
        onClick={toggleSection}
        style={{ cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', margin: '10px 0' }}
      >
        <span>{isOpen ? '▼' : '►'} Fuel Prices & Data</span>
      </div>

      <div
        className={`dashboard-container1 ${isOpen ? 'open' : 'closed'}`}
      >
        {/* ETU Price display */}
        {etuPrice && (
          <div className="etu-price-container">
            <h3>EU ETS Price Today</h3>
            <p>{etuPrice.price}$ (USD/mt)</p>
          </div>
        )}

        <div className="etu-price-container1">
          <h3>VLSFO Average Global</h3>
          <p><h4>{globalPortsAverage}$ (USD/mt)</h4></p>
        </div>
        <div className="etu-price-container1">
          <h3>MGO Average Global</h3>
          <p><h4>{globalPortsMGO}$ (USD/mt)</h4></p>
        </div>
        <div className="etu-price-container1">
          <h3>IFO380 Average Global</h3>
          <p><h4>{globalPortsIFO}$ (USD/mt)</h4></p>
        </div>

        <div className="dropdown-container">
          <label htmlFor="fuelTypeDropdown" className="dropdown-label">Select Fuel Type:</label>
          <select
            id="fuelTypeDropdown"
            value={selectedFuelType}
            onChange={(e) => setSelectedFuelType(e.target.value)}
            className="fuel-type-dropdown"
          >
            {fuelTypes.map((fuelType, index) => (
              <option key={index} value={fuelType}>
                {fuelType}
              </option>
            ))}
          </select>
        </div>

        <div className="fuel-prices-table">
          <h2>Fuel Prices Today</h2>
          <table>
            <thead>
              <tr>
                <th>Location</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              {fuelPrices
                .filter((row) => selectedFuelType === 'All' || row[1] === selectedFuelType)
                .map((row, index) => (
                  <tr key={index}>
                    <td>{row[0]}</td>
                    <td>{row[2]}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>   
      
  );
}

export default EnvironmentalImpact;